import { Component, EventEmitter, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Actions, Select, Store } from '@ngxs/store';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { User } from 'app/projects/user/src/lib/models/user';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { SortAllUsers } from '../../../../all-users/actions/index';
import { FeatureService } from '../../../../feature/services/feature.service';
import { MatStepper } from '@angular/material/stepper';
import { TranslateService } from '@ngx-translate/core';
import { AllUserDataSource } from 'app/library/all-users/components/active-user-list/datasource';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { CourseOfferingExecutionsState } from 'app/library/course-offering-executions/models/course-offering-executions.state';
import { Module } from 'app/library/module/models/module.model';

@Component({
    selector: 'app-add-course-offering-bookings-dialog',
    templateUrl: './add-course-offering-bookings-dialog.component.html',
    styleUrls: ['./add-course-offering-bookings-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class AddCourseOfferingBookingsDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    @Select(CourseOfferingExecutionsState.getEntities)
    execution$: Observable<Module[]>;
    executions: Module[] = [];

    @Input()
    entitie$: Observable<User[]>;

    @Input()
    moduleId: string;

    @Input()
    title: string;

    @Input()
    role: string;

    @Input()
    selectedUsersList: User[] = [];

    @Input()
    showFirstStep: boolean = false;

    get shouldShowNextButton(): boolean {
        if (!this.stepper) {
            return false;
        }

        if (this.showFirstStep) {
            return this.stepper.selectedIndex === 0 || this.stepper.selectedIndex === 1;
        }

        return this.stepper.selectedIndex === 0;
    }

    get isLastStep(): boolean {
        return this.stepper?.steps && this?.stepper.selectedIndex === this?.stepper.steps.length - 1;
    }

    get canGoToNotificationStep(): boolean {
        return !!this.selectedExecution.value;
    }

    @ViewChild(MatStepper) stepper: MatStepper;

    onConfirm = new EventEmitter<User>();

    filters: {
        name: string;
        translationKey: string;
        isUserAttribute: boolean;
    }[] = [
        { name: 'module', translationKey: 'MODULE.SINGULAR', isUserAttribute: false },
        { name: 'userRole', translationKey: 'GENERAL.LABEL.USER_ROLE', isUserAttribute: false },
    ];

    notifyMembers = true;
    dataSource: AllUserDataSource;
    displayedColumns = [];
    removable = true;
    searchInput = new FormControl();
    selectedExecution = new FormControl();
    hideEmail: boolean;
    roleLabel: string;
    isCourseCatalogFeatureEnabled: boolean;

    @Select(AllUsersState.getTotalCount)
    totalCount$: Observable<number>;

    @ViewChild(MatPaginator)
    paginator: MatPaginator;
    @ViewChild(MatSort)
    sort: MatSort;

    constructor(
        private _store: Store,
        public dialogRef: MatDialogRef<AddCourseOfferingBookingsDialogComponent>,
        private _action$: Actions,
        private _featureService: FeatureService,
        private _translateService: TranslateService
    ) {
        super();

        this.hideEmail = this._featureService.checkFeatureStatus(FeatureSwitchName.ParticipantEmailVisibility, 'inactive');
        this.isCourseCatalogFeatureEnabled = this._featureService.checkFeatureStatus(FeatureSwitchName.CourseCatalog);
    }

    async ngOnInit(): Promise<void> {
        this.searchInput.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(1000), distinctUntilChanged()).subscribe((searchText) => {
            this.loadUsersPage(true);
        });

        this.execution$
            .pipe(
                filter((executions) => !!executions),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((executions) => {
                this.executions = executions.filter((execution) => execution.status !== 'planned');
            });

        if (this.showFirstStep) {
            if (this.selectedUsersList.length) {
                this.dataSource = new AllUserDataSource(
                    this.entitie$.pipe(
                        map((users) => {
                            return users?.map((item) => {
                                if (this.selectedUsersList.find((item2) => item2.id === item.id)) {
                                    item = Object.assign(new User(), item, {
                                        selected: true,
                                    });
                                }
                                return item;
                            });
                        })
                    ),
                    this._store,
                    this._action$
                );
            } else {
                this.dataSource = new AllUserDataSource(this.entitie$, this._store, this._action$);
            }

            setTimeout(() => {
                this.loadUsersPage(false);

                this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll)).subscribe((sort) => {
                    this._store.dispatch(new SortAllUsers(sort));

                    this.loadUsersPage(true);
                });

                this.paginator.page.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
                    this.loadUsersPage(false);
                });
            });
        }

        if (this.hideEmail) {
            this.displayedColumns = ['avatar', 'firstName', 'lastName', 'buttons'];
        } else {
            this.displayedColumns = ['avatar', 'firstName', 'lastName', 'email', 'buttons'];
        }
    }

    goForward(stepper: MatStepper): void {
        if (this.showFirstStep && stepper.selectedIndex === 0 && !this.selectedUsersList.length) {
            return;
        }

        if ((this.showFirstStep && stepper.selectedIndex === 1) || (!this.showFirstStep && stepper.selectedIndex === 0)) {
            if (!this.canGoToNotificationStep) {
                return;
            }
        }

        stepper.next();
    }

    async onStepChange(stepper: MatStepper): Promise<void> {
        const currentIndex = this.showFirstStep ? stepper.selectedIndex : stepper.selectedIndex + 1;

        if (currentIndex === 0) {
            this.title = this._translateService.instant('PAGE.DIALOG.ADD_PROGRAM_PARTICIPANTS.LABEL.CHOOSE_PARTICIPANTS');
        } else if (currentIndex === 1) {
            this.title = this._translateService.instant('PROGRAM_OFFERING.BUTTON.BOOK_INTO_EXECUTIONS');
        } else if (currentIndex === 2) {
            this.title = this._translateService.instant('GENERAL.LABEL.NOTIFICATION');
        }
    }

    removeFromList(user: User): void {
        this.selectedUsersList = [...this.selectedUsersList.filter((item) => item.id !== user.id)];

        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((moduleUser) => {
                        if (moduleUser.id === user.id) {
                            moduleUser = Object.assign(new User(), moduleUser, {
                                selected: false,
                            });
                        } else if (this.selectedUsersList.find((user) => user.id === moduleUser.id)) {
                            moduleUser = Object.assign(new User(), moduleUser, {
                                selected: true,
                            });
                        }
                        return moduleUser;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    addToList(user: User): void {
        this.selectedUsersList = [...this.selectedUsersList, user];
        this.dataSource = new AllUserDataSource(
            this.entitie$.pipe(
                map((users) => {
                    return users.map((moduleUser) => {
                        if (moduleUser.id === user.id || this.selectedUsersList.find((user) => user.id === moduleUser.id)) {
                            moduleUser = Object.assign(new User(), moduleUser, {
                                selected: true,
                            });
                        }
                        return moduleUser;
                    });
                })
            ),
            this._store,
            this._action$
        );
    }

    saveSelected(): void {
        this.dialogRef.close({ list: this.selectedUsersList, selectedExecution: this.selectedExecution.value, notify: this.notifyMembers });
    }

    isExecutionSelectable(execution: Module): boolean {
        if (execution?.selectionStatus) {
            return execution.selectionStatus.isSelectable;
        }

        if (!this.isCourseCatalogFeatureEnabled) {
            return true;
        }

        return !execution?.listingId;
    }

    confirm(user: User): void {
        this.onConfirm.next(user);
    }

    async loadUsersPage(resetPageIndex = false): Promise<void> {
        if (resetPageIndex) {
            this.paginator.pageIndex = 0;
        }

        if (this.dataSource) {
            this.dataSource.loadUsers(
                'courseOfferingUsers',
                [],
                this.moduleId,
                this.searchInput.value,
                this.sort.active,
                this.sort.direction,
                this.paginator.pageSize,
                this.paginator.pageIndex + 1
            );
        }
    }
}
